(() => {
  if (!window.location.pathname.includes('/products/') && !document.querySelector('.joa-earrings-product, .joa-bracelet-product, .joa-necklace-product')) return;

  const OPTION_SELECTOR = '#MainContent .mw-apo-container:not(.joa-quick-ring .mw-apo-container, .mw-tingle .mw-apo-container, dialog .mw-apo-container)';
  const ADD_TO_CART_SELECTOR = [
    '.joa-sticky-purchase',
    '.mw-apo-add-to-cart-cloned',
    'product-form-component button[data-testid="standalone-add-to-cart"]',
    'product-form-component button[name="add"].add-to-cart-button',
    '.sticky-add-to-cart__button',
    '.mw-apo-sticky-add-to-cart-cloned'
  ].join(', ');
  const STICKY_ADD_TO_CART_SELECTOR = [
    '.joa-sticky-purchase',
    '.sticky-add-to-cart__button',
    '.mw-apo-sticky-add-to-cart-cloned'
  ].join(', ');
  const ADD_CONFIRMATION_DURATION = 1600;
  const ADD_LOADING_MIN_DURATION = 650;
  const MATERIAL_WORDS = /\b(material|metal)\b/i;
  const MATERIAL_VALUES = /\b(gold|platinum|tone)\b/i;
  const mobileProductLayout = window.matchMedia('(max-width: 749px)');
  const productHeaderHomes = new WeakMap();

  let scheduled = false;
  let cartToastTimer = null;
  let cartToastHideTimer = null;
  let cartToastRemaining = 8000;
  let cartToastStartedAt = 0;
  let cartSnapshot = null;
  let cartSnapshotPromise = null;
  let pendingAdd = null;
  let addIntentGeneration = 0;
  let cartConfirmationInFlight = 0;
  let feedbackState = 'idle';
  let feedbackTimer = null;
  const feedbackLabels = new WeakMap();

  function syncCartFeedback() {
    const productTitle = document.querySelector('.product-details h1')?.textContent.trim() || '';
    const isJewelry = Boolean(document.querySelector('.joa-earrings-product, .joa-bracelet-product, .joa-necklace-product')) ||
      /bracelet|necklace|earring|^custom (?:engraving|pav[eé] initials)$/i.test(productTitle);
    document.querySelectorAll('[data-joa-cart-feedback]').forEach((button) => {
      const label = button.querySelector('[data-joa-loading-label]');
      const loadingText = isJewelry ? button.dataset.joaItemLoading : button.dataset.joaRingLoading;
      if (label && label.textContent !== loadingText) label.textContent = loadingText;
      if (button.dataset.joaCartState !== feedbackState) button.dataset.joaCartState = feedbackState;
      if (feedbackState === 'success') {
        if (!feedbackLabels.has(button)) feedbackLabels.set(button, button.getAttribute('aria-label'));
        if (button.dataset.added !== 'true') button.dataset.added = 'true';
        if (button.getAttribute('aria-label') !== button.dataset.joaAddedLabel) button.setAttribute('aria-label', button.dataset.joaAddedLabel);
      } else {
        button.removeAttribute('data-added');
        if (feedbackLabels.has(button)) {
          const previous = feedbackLabels.get(button);
          if (previous == null) button.removeAttribute('aria-label');
          else button.setAttribute('aria-label', previous);
          feedbackLabels.delete(button);
        }
      }
    });
  }

  function setCartFeedback(state) {
    window.clearTimeout(feedbackTimer);
    feedbackState = state;
    syncCartFeedback();
    document.dispatchEvent(new CustomEvent('joa:cart-feedback', { detail: { state } }));
    if (state === 'success') feedbackTimer = window.setTimeout(() => setCartFeedback('idle'), ADD_CONFIRMATION_DURATION);
  }

  function inViewport(element) {
    if (!isVisiblyRenderedButton(element)) return false;
    const rect = element.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < innerHeight && rect.right > 0 && rect.left < innerWidth;
  }

  async function flyConfirmedItem(intent, imageUrl) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!customElements.get('fly-to-cart')) {
      await Promise.race([customElements.whenDefined('fly-to-cart'), new Promise(resolve => window.setTimeout(resolve, 2000))]);
    }
    if (!customElements.get('fly-to-cart') || document.hidden) return;
    const destination = document.querySelector('.header-actions__cart-icon');
    if (!destination) return;
    const source = [intent.button, ...document.querySelectorAll('[data-joa-cart-feedback]')].find(inViewport);
    if (!source) return;
    const flight = document.createElement('fly-to-cart');
    flight.className = addButtonSurface(source) === 'sticky' ? 'fly-to-cart--sticky' : 'fly-to-cart--main';
    if (imageUrl) {
      flight.style.setProperty('background-image', `url(${JSON.stringify(imageUrl)})`);
    } else {
      const icon = source.querySelector('.add-to-cart-icon svg');
      if (!icon) return;
      flight.classList.add('joa-cart-flight-fallback');
      flight.appendChild(icon.cloneNode(true));
    }
    flight.style.setProperty('--start-opacity', '0');
    flight.source = source;
    flight.destination = destination;
    document.body.appendChild(flight);
  }

  function cartUrl() {
    if (window.Theme?.routes?.cart_url) return window.Theme.routes.cart_url;
    const root = String(window.Shopify?.routes?.root || '/');
    return `${root.endsWith('/') ? root : `${root}/`}cart`;
  }

  function cartJsonUrl() {
    return `${cartUrl()}.js`;
  }

  async function fetchCart() {
    const response = await fetch(cartJsonUrl(), {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
    return response.json();
  }

  function cartLineQuantities(cart) {
    const quantities = new Map();
    for (const item of Array.isArray(cart?.items) ? cart.items : []) {
      const key = String(item?.key || item?.variant_id || '');
      if (!key) continue;
      quantities.set(key, Number(item?.quantity || 0));
    }
    return quantities;
  }

  function comparableProductTitle(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/#mws options\s*\d*/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function visibleCartCount() {
    const value = document.querySelector('.cart-bubble__text-count')?.textContent;
    const count = Number.parseInt(String(value || '').trim(), 10);
    return Number.isFinite(count) ? count : null;
  }

  function primeCartSnapshot() {
    if (cartSnapshotPromise) return cartSnapshotPromise;
    cartSnapshotPromise = fetchCart()
      .then((cart) => {
        if (!pendingAdd) cartSnapshot = cart;
        return cart;
      })
      .catch(() => null)
      .finally(() => {
        cartSnapshotPromise = null;
      });
    return cartSnapshotPromise;
  }

  function hideCartToast() {
    const toast = document.querySelector('[data-joa-cart-toast]');
    if (!toast) return;

    window.clearTimeout(cartToastTimer);
    window.clearTimeout(cartToastHideTimer);
    cartToastTimer = null;
    toast.classList.remove('is-visible');
    cartToastHideTimer = window.setTimeout(() => {
      if (!toast.classList.contains('is-visible')) toast.hidden = true;
      cartToastHideTimer = null;
    }, 200);
  }

  function startCartToastTimer(delay = 8000) {
    window.clearTimeout(cartToastTimer);
    cartToastRemaining = Math.max(0, delay);
    cartToastStartedAt = Date.now();
    cartToastTimer = window.setTimeout(hideCartToast, cartToastRemaining);
  }

  function pauseCartToastTimer() {
    if (!cartToastTimer) return;
    window.clearTimeout(cartToastTimer);
    cartToastTimer = null;
    cartToastRemaining = Math.max(
      0,
      cartToastRemaining - (Date.now() - cartToastStartedAt)
    );
  }

  function ensureCartToast() {
    let toast = document.querySelector('[data-joa-cart-toast]');
    if (toast) return toast;

    toast = document.createElement('aside');
    toast.className = 'joa-cart-toast';
    toast.dataset.joaCartToast = '';
    toast.setAttribute('role', 'region');
    toast.setAttribute('aria-label', 'Cart confirmation');
    toast.hidden = true;

    const media = document.createElement('span');
    media.className = 'joa-cart-toast__media';
    media.setAttribute('aria-hidden', 'true');

    const image = document.createElement('img');
    image.className = 'joa-cart-toast__image';
    image.alt = '';

    const check = document.createElement('span');
    check.className = 'joa-cart-toast__check';
    check.textContent = '✓';
    media.append(image, check);

    const copy = document.createElement('span');
    copy.className = 'joa-cart-toast__copy';

    const heading = document.createElement('span');
    heading.className = 'joa-cart-toast__heading';
    heading.textContent = 'Added to cart';

    const title = document.createElement('span');
    title.className = 'joa-cart-toast__title';
    title.dataset.joaCartToastTitle = '';
    copy.append(heading, title);

    const link = document.createElement('a');
    link.className = 'joa-cart-toast__link';
    link.dataset.joaCartToastLink = '';
    link.href = cartUrl();
    link.textContent = 'View cart';

    const dismiss = document.createElement('button');
    dismiss.type = 'button';
    dismiss.className = 'joa-cart-toast__dismiss';
    dismiss.setAttribute('aria-label', 'Dismiss cart notification');
    dismiss.textContent = '×';
    dismiss.addEventListener('click', hideCartToast);

    toast.addEventListener('mouseenter', pauseCartToastTimer);
    toast.addEventListener('mouseleave', () => {
      if (!toast.contains(document.activeElement)) {
        startCartToastTimer(cartToastRemaining || 8000);
      }
    });
    toast.addEventListener('focusin', pauseCartToastTimer);
    toast.addEventListener('focusout', (event) => {
      if (!toast.contains(event.relatedTarget)) {
        startCartToastTimer(cartToastRemaining || 8000);
      }
    });

    toast.append(media, copy, link, dismiss);
    document.body.appendChild(toast);
    return toast;
  }

  function announceCartAddition(productTitle, itemCount) {
    let announcer = document.querySelector('[data-joa-cart-announcer]');
    if (!announcer) {
      announcer = document.createElement('div');
      announcer.className = 'joa-cart-announcer';
      announcer.dataset.joaCartAnnouncer = '';
      announcer.setAttribute('role', 'status');
      announcer.setAttribute('aria-live', 'polite');
      announcer.setAttribute('aria-atomic', 'true');
      document.body.appendChild(announcer);
    }

    announcer.textContent = '';
    window.requestAnimationFrame(() => {
      const countCopy = Number.isFinite(itemCount)
        ? ` Cart now contains ${itemCount} item${itemCount === 1 ? '' : 's'}.`
        : '';
      announcer.textContent = `${productTitle} added to cart.${countCopy}`;
    });
  }

  function addButtonSurface(button) {
    return button?.matches?.(STICKY_ADD_TO_CART_SELECTOR) ? 'sticky' : 'main';
  }

  function isVisiblyRenderedButton(button) {
    if (
      !button?.isConnected ||
      button.hidden ||
      button.matches?.(
        '.mw-apo-add-to-cart-hide, .mw-apo-sticky-add-to-cart-hide, .mw-hidden'
      ) ||
      button.closest?.('[hidden], [aria-hidden="true"]')
    ) return false;

    const rect = button.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1) return false;

    for (let node = button; node instanceof HTMLElement; node = node.parentElement) {
      const style = window.getComputedStyle(node);
      if (
        style.display === 'none' ||
        style.visibility === 'hidden' ||
        Number(style.opacity) === 0
      ) return false;
    }
    return true;
  }

  function visibleButtonForSurface(surface) {
    return Array.from(document.querySelectorAll(ADD_TO_CART_SELECTOR)).find(
      (button) =>
        addButtonSurface(button) === surface &&
        isVisiblyRenderedButton(button)
    ) || null;
  }

  function markAddToCartIntent(event) {
    const origin = event.submitter || event.target;
    const originButton = origin?.closest?.(ADD_TO_CART_SELECTOR) ||
      (event.type === 'submit'
        ? event.target.querySelector?.(ADD_TO_CART_SELECTOR)
        : null);
    if (!originButton || originButton.closest('.joa-quick-ring') || originButton.disabled || originButton.getAttribute('aria-disabled') === 'true' || feedbackState === 'success' || document.body.classList.contains('joa-options-are-loading')) return;
    const form = originButton.form || document.querySelector('.product-details form[action*="/cart/add"]');
    if (form && !form.checkValidity()) return;

    /* APO forwards the visible clone's activation to a hidden original after
     * it finishes creating the configured variant. Never let that internal
     * click replace the customer's still-pending visible-button intent. */
    if (pendingAdd) return;

    const surface = addButtonSurface(originButton);
    const button = isVisiblyRenderedButton(originButton)
      ? originButton
      : visibleButtonForSurface(surface);
    if (!button) return;

    const baselineCount = Number(cartSnapshot?.item_count);
    const generation = ++addIntentGeneration;
    const expiryTimer = window.setTimeout(() => {
      if (pendingAdd?.generation === generation) {
        pendingAdd = null;
        setCartFeedback('idle');
      }
    }, 60000);

    pendingAdd = {
      generation,
      button,
      startedAt: Date.now(),
      baselineCount: Number.isFinite(baselineCount)
        ? baselineCount
        : (visibleCartCount() ?? 0),
      baselineLines: cartLineQuantities(cartSnapshot),
      expectedTitle: comparableProductTitle(
        document.querySelector('.product-details h1')?.textContent
      ),
      expiryTimer
    };
    queueMicrotask(() => {
      if (pendingAdd?.generation === generation) setCartFeedback('loading');
    });
  }

  function blockConfirmedButtonActivation(event) {
    const button = event.target.closest?.(ADD_TO_CART_SELECTOR);
    if (!button || button.closest('.joa-quick-ring') || !event.isTrusted || feedbackState === 'idle') return;
    if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  function addedLineFromCart(intent, cart) {
    const itemCount = Number(cart?.item_count);
    if (!Number.isFinite(itemCount) || itemCount <= intent.baselineCount) {
      return null;
    }

    const items = Array.isArray(cart?.items) ? cart.items : [];
    const increasedItems = items.filter((item) => {
      const key = String(item?.key || item?.variant_id || '');
      return Number(item?.quantity || 0) > Number(intent.baselineLines.get(key) || 0);
    });
    return increasedItems.find((item) => {
      if (!intent.expectedTitle) return true;
      return comparableProductTitle(item?.product_title) === intent.expectedTitle;
    }) || null;
  }

  async function verifiedCartAfterAdd(intent, initialCart) {
    const delays = [0, 250, 700, 1500, 2800];
    for (let index = 0; index < delays.length; index += 1) {
      if (pendingAdd?.generation !== intent.generation) return null;
      if (delays[index]) {
        await new Promise((resolve) => window.setTimeout(resolve, delays[index]));
      }

      let cart = index === 0 ? initialCart : null;
      if (!cart || !Array.isArray(cart.items)) {
        try {
          cart = await fetchCart();
        } catch (_error) {
          continue;
        }
      }

      const item = addedLineFromCart(intent, cart);
      if (item) return { cart, item };
    }
    return null;
  }

  async function showCartToast(event) {
    const detail = event.detail || {};
    const data = detail.data || {};
    const addedQuantity = Number(data.itemCount);

    if (data.didError === true) {
      handleCartError();
      return;
    }
    if (Number.isFinite(addedQuantity) && addedQuantity < 1 && !pendingAdd) return;

    const intent = pendingAdd;
    if (!intent) {
      if (detail.resource?.items) cartSnapshot = detail.resource;
      else primeCartSnapshot();
      return;
    }
    if (cartConfirmationInFlight === intent.generation) return;
    cartConfirmationInFlight = intent.generation;

    const verified = await verifiedCartAfterAdd(intent, detail.resource);
    if (cartConfirmationInFlight === intent.generation) {
      cartConfirmationInFlight = 0;
    }
    if (!verified || pendingAdd?.generation !== intent.generation) return;

    const loadingRemaining = ADD_LOADING_MIN_DURATION - (Date.now() - intent.startedAt);
    if (loadingRemaining > 0) {
      await new Promise((resolve) => window.setTimeout(resolve, loadingRemaining));
      if (pendingAdd?.generation !== intent.generation) return;
    }

    const { cart, item } = verified;
    window.clearTimeout(intent.expiryTimer);
    pendingAdd = null;
    cartSnapshot = cart;

    const items = Array.isArray(cart?.items) ? cart.items : [];
    const sourceId = String(detail.sourceId || '');
    const productId = String(data.productId || '');
    const describedItem = items.find(
      (candidate) => String(candidate?.variant_id || '') === sourceId
    ) || items.find(
      (candidate) => String(candidate?.product_id || '') === productId
    ) || item;

    const toast = ensureCartToast();
    const title = toast.querySelector('[data-joa-cart-toast-title]');
    const link = toast.querySelector('[data-joa-cart-toast-link]');
    const image = toast.querySelector('.joa-cart-toast__image');
    const check = toast.querySelector('.joa-cart-toast__check');
    const imageUrl = String(
      describedItem?.featured_image?.url || describedItem?.image || ''
    );
    const pageImageUrl = String(
      document.querySelector(
        '.product-information__media .product-media__image img, .product-media-gallery img'
      )?.currentSrc ||
      document.querySelector(
        '.product-information__media .product-media__image img, .product-media-gallery img'
      )?.src ||
      ''
    );
    const productTitle = String(
      describedItem?.product_title ||
      document.querySelector('.product-details h1')?.textContent ||
      'Item'
    ).replace(/\s+/g, ' ').trim();
    const itemCount = Number(cart?.item_count);
    const toastImageUrl = imageUrl || pageImageUrl;

    setCartFeedback('success');
    flyConfirmedItem(intent, toastImageUrl).catch(() => {});
    if (describedItem?.handle) document.dispatchEvent(new CustomEvent('joa:cart-add-confirmed', { detail: { handle: describedItem.handle } }));
    announceCartAddition(productTitle, itemCount);

    title.textContent = productTitle;
    link.href = cartUrl();
    link.textContent = Number.isFinite(itemCount)
      ? `View cart (${itemCount})`
      : 'View cart';
    image.hidden = !toastImageUrl;
    check.hidden = Boolean(toastImageUrl);
    if (toastImageUrl) image.src = toastImageUrl;
    else image.removeAttribute('src');

    window.clearTimeout(cartToastHideTimer);
    cartToastHideTimer = null;
    toast.hidden = false;
    window.requestAnimationFrame(() => toast.classList.add('is-visible'));
    startCartToastTimer(8000);
  }

  function primaryOptionContainer() {
    return [...document.querySelectorAll(OPTION_SELECTOR)].find(
      (container) =>
        !container.closest('.joa-express-form') &&
        container.querySelector('[name^="mw_opts["]')
    ) || null;
  }

  function syncPrimaryPrice() {
    const host = document.querySelector('.product-details product-price');
    const display = window.JoaPriceDisplay?.read(host);
    if (!host || !display?.current) return;
    let target = host.querySelector(':scope > .joa-product-price-display');
    if (!target) {
      target = document.createElement('div');
      target.className = 'joa-product-price-display';
      host.append(target);
    }
    window.JoaPriceDisplay.render(target, display);
    host.classList.add('joa-price-enhanced');
  }

  function selectedProductPrice() {
    const display = window.JoaPriceDisplay?.read(document.querySelector('.product-details product-price'));
    if (display?.current) return display.current;
    return (
      document.querySelector('product-price .price__sale:not(.price__hidden) .price-item--sale') ||
      document.querySelector('product-price .price__regular .price') ||
      document.querySelector('product-price .price')
    )?.textContent?.replace(/\s+/g, ' ').trim() || '';
  }

  function syncStickyPrice() {
    const stickyPrice = document.querySelector('.sticky-add-to-cart__price');
    if (stickyPrice?.closest('sticky-add-to-cart') && customElements.get('sticky-add-to-cart')) return;
    const display = window.JoaPriceDisplay?.read(document.querySelector('.product-details product-price'));
    if (stickyPrice && display?.current) {
      stickyPrice.setAttribute('aria-live', 'polite');
      window.JoaPriceDisplay.render(stickyPrice, display);
      return;
    }
    const priceText = selectedProductPrice();
    if (!(stickyPrice instanceof HTMLElement) || !priceText) return;

    stickyPrice.setAttribute('aria-live', 'polite');
    if (stickyPrice.textContent?.replace(/\s+/g, ' ').trim() !== priceText) {
      stickyPrice.textContent = priceText;
    }
  }

  function purchaseBreakdown(priceText) {
    const normalizedText = priceText.replace(/[\u00a0\u202f]/g, ' ').trim();
    const numberMatch = normalizedText.match(/-?\d[\d\s.,'’]*/);

    if (!numberMatch) return null;

    const rawNumber = numberMatch[0].trim();
    const lastDot = rawNumber.lastIndexOf('.');
    const lastComma = rawNumber.lastIndexOf(',');
    const lastSeparator = Math.max(lastDot, lastComma);
    const digitsAfterSeparator = lastSeparator >= 0
      ? rawNumber.slice(lastSeparator + 1).replace(/\D/g, '').length
      : 0;
    const currencyCode = normalizedText.match(/\b[A-Z]{3}\b/)?.[0];
    let currencyDecimalPlaces = null;

    try {
      if (currencyCode) {
        currencyDecimalPlaces = new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: currencyCode
        }).resolvedOptions().maximumFractionDigits;
      }
    } catch (_error) {
      currencyDecimalPlaces = null;
    }

    const decimalSeparator = lastSeparator >= 0 && (
      (lastDot >= 0 && lastComma >= 0) ||
      digitsAfterSeparator === 1 ||
      digitsAfterSeparator === 2 ||
      (currencyDecimalPlaces > 2 && digitsAfterSeparator === currencyDecimalPlaces)
    )
      ? rawNumber[lastSeparator]
      : '';
    const decimalPlaces = decimalSeparator ? digitsAfterSeparator : 0;
    const integerPart = decimalSeparator ? rawNumber.slice(0, lastSeparator) : rawNumber;
    const fractionPart = decimalSeparator ? rawNumber.slice(lastSeparator + 1) : '';
    const parsedAmount = Number(
      `${integerPart.replace(/\D/g, '') || '0'}${fractionPart ? `.${fractionPart.replace(/\D/g, '')}` : ''}`
    );

    if (!Number.isFinite(parsedAmount)) return null;

    const numberStart = normalizedText.indexOf(numberMatch[0]);
    const prefix = normalizedText.slice(0, numberStart);
    const suffix = normalizedText.slice(numberStart + rawNumber.length);
    const suffixWithoutCode = suffix.replace(/\s*\b[A-Z]{3}\b\s*$/, '');
    const visibleSuffix = prefix || suffixWithoutCode.trim() || !currencyCode
      ? suffixWithoutCode
      : ` ${currencyCode}`;
    const scale = 10 ** decimalPlaces;
    const totalMinorUnits = Math.round(parsedAmount * scale);
    const depositMinorUnits = Math.round(totalMinorUnits * 0.1);
    const remainingMinorUnits = totalMinorUnits - depositMinorUnits;

    function formatAmount(minorUnits) {
      const amount = minorUnits / scale;
      let formattedAmount = amount.toFixed(decimalPlaces);

      try {
        formattedAmount = new Intl.NumberFormat(document.documentElement.lang || 'en', {
          minimumFractionDigits: decimalPlaces,
          maximumFractionDigits: decimalPlaces,
          useGrouping: false
        }).format(amount);
      } catch (_error) {
        // The fixed-point value above is a safe fallback for older browsers.
      }

      return `${prefix}${formattedAmount}${visibleSuffix}`.replace(/\s+/g, ' ').trim();
    }

    return {
      now: formatAmount(depositMinorUnits),
      later: formatAmount(remainingMinorUnits)
    };
  }

  function optionName(option) {
    const title = option.querySelector('.mw-text--style-strong');
    if (!title) return '';

    const cleanTitle = title.cloneNode(true);
    cleanTitle.querySelectorAll('.custom-ring-size, .joa-option-quick-wrap').forEach((node) => node.remove());

    return cleanTitle.textContent
      ?.replace(/\s+/g, ' ')
      .replace(/[.:]+$/, '')
      .trim() || '';
  }

  function originalSelect(option) {
    return option.querySelector('select:not(.joa-option-quick-select)');
  }

  function optionValueLabel(value) {
    const imageAlt = value.querySelector('img[alt]')?.getAttribute('alt')?.trim();
    if (imageAlt) return imageAlt;

    const tooltipLabel = value
      .querySelector('[aria-label]')
      ?.getAttribute('aria-label')
      ?.trim();
    if (tooltipLabel) return tooltipLabel;

    return value.textContent?.replace(/\s+/g, ' ').trim() || '';
  }

  function materialOptionAbbreviation(labelText) {
    if (/\b(platinum|plat)\b/i.test(labelText)) return 'PLAT';

    const karat = labelText.match(/\b(14|18)\s*k\b/i);
    return karat ? `${karat[1]}K` : labelText;
  }

  function selectedOptionValue(option) {
    const select = originalSelect(option);

    if (select) {
      const selectedText = select.selectedOptions?.[0]?.textContent?.replace(/\s+/g, ' ').trim() || '';
      return /^--\s*please select\s*--$/i.test(selectedText) ? '' : selectedText;
    }

    const checkedValues = [...option.querySelectorAll('input:checked')]
      .map((control) => control.closest('.mw-option__value'))
      .filter(Boolean);

    if (checkedValues.length) {
      return checkedValues
        .map((value) => value.dataset.joaLabel || optionValueLabel(value))
        .filter(Boolean)
        .join(', ');
    }

    const selectedValue = option.querySelector('.mw-option__value--selected');
    if (selectedValue) return selectedValue.dataset.joaLabel || optionValueLabel(selectedValue);

    const textControl = option.querySelector('input:not([type="radio"]):not([type="checkbox"]), textarea');
    return textControl?.value?.replace(/\s+/g, ' ').trim() || '';
  }

  function optionChoices(option) {
    const select = originalSelect(option);

    if (select) {
      return [...select.options]
        .filter((choice) => choice.value && !choice.dataset.hidden)
        .map((choice) => ({
          value: choice.value,
          label: choice.textContent?.replace(/\s+/g, ' ').trim() || choice.value,
          disabled: choice.disabled
        }));
    }

    const controls = [...option.querySelectorAll('input.mw-option__control[type="radio"]')];
    return controls.map((control) => {
      const value = control.closest('.mw-option__value');
      return {
        value: control.value,
        label: value?.dataset.joaLabel || (value ? optionValueLabel(value) : control.value),
        disabled: control.disabled
      };
    });
  }

  function selectedOptionKey(option) {
    const select = originalSelect(option);
    if (select) return select.value;
    return option.querySelector('input.mw-option__control[type="radio"]:checked')?.value || '';
  }

  function applyQuickSelection(option, selectedKey) {
    const select = originalSelect(option);

    if (select) {
      if (select.value === selectedKey) return;
      select.value = selectedKey;
      select.dispatchEvent(new Event('input', { bubbles: true }));
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const control = [...option.querySelectorAll('input.mw-option__control[type="radio"]')]
      .find((input) => input.value === selectedKey);

    if (control && !control.checked && !control.disabled) control.click();
  }

  function decorateQuickSelector(option, label, summary) {
    const labelItem = label.parentElement;
    const headingShell = labelItem?.parentElement;
    const choices = optionChoices(option);
    const optionLabel = option.dataset.joaOptionName || optionName(option) || 'option';
    const isRingSize = /\bring size\b/i.test(optionLabel);

    if (document.body.classList.contains('joa-product-design')) {
      headingShell?.querySelector(':scope > .joa-option-quick-wrap')?.remove();
      option.classList.remove('joa-option--has-quick-select');
      option.classList.toggle('joa-option--hide-heading-value', isRingSize);
      summary.removeAttribute('aria-hidden');
      return;
    }

    if (isRingSize) {
      headingShell?.querySelector(':scope > .joa-option-quick-wrap')?.remove();
      option.classList.remove('joa-option--has-quick-select');
      option.classList.add('joa-option--hide-heading-value');
      summary.setAttribute('aria-hidden', 'true');
      return;
    }

    option.classList.remove('joa-option--hide-heading-value');

    if (!headingShell || choices.length < 2) {
      headingShell?.querySelector(':scope > .joa-option-quick-wrap')?.remove();
      option.classList.remove('joa-option--has-quick-select');
      summary.removeAttribute('aria-hidden');
      return;
    }

    headingShell.classList.add('joa-option-heading-shell');
    option.classList.add('joa-option--has-quick-select');

    let quickWrap = headingShell.querySelector(':scope > .joa-option-quick-wrap');
    if (!quickWrap) {
      quickWrap = document.createElement('div');
      quickWrap.className = 'mw-stack__item joa-option-quick-wrap';

      const quickSelect = document.createElement('select');
      quickSelect.className = 'joa-option-quick-select';
      quickSelect.addEventListener('change', () => {
        applyQuickSelection(option, quickSelect.value);
        quickSelect.blur();
      });

      quickWrap.append(quickSelect);
      headingShell.append(quickWrap);
    }

    const quickSelect = quickWrap.querySelector('.joa-option-quick-select');
    const selectedKey = selectedOptionKey(option);
    const choiceSignature = JSON.stringify({
      choices,
      hasSelection: Boolean(selectedKey)
    });

    quickSelect.setAttribute('aria-label', `Change ${optionLabel}`);
    quickSelect.title = `Change ${optionLabel}`;

    if (quickSelect.dataset.joaChoiceSignature !== choiceSignature) {
      quickSelect.textContent = '';
      if (!selectedKey) {
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = 'Select';
        placeholder.disabled = true;
        placeholder.selected = true;
        quickSelect.append(placeholder);
      }
      choices.forEach((choice) => {
        const quickChoice = document.createElement('option');
        quickChoice.value = choice.value;
        quickChoice.textContent = choice.label;
        quickChoice.disabled = choice.disabled;
        quickSelect.append(quickChoice);
      });
      quickSelect.dataset.joaChoiceSignature = choiceSignature;
    }

    if (quickSelect.value !== selectedKey) quickSelect.value = selectedKey;
    summary.setAttribute('aria-hidden', 'true');
  }

  function decorateOptionHeading(option) {
    const label = option.querySelector(':scope > .mw-stack > .mw-stack__item:first-child label.mw-label');
    const heading = label?.querySelector(':scope > .mw-stack');
    const main = heading?.querySelector(':scope > .mw-stack__item:first-child');

    if (!heading || !main) return;

    let summary = main.querySelector(':scope > .mw-accumulated-title');
    if (!summary) {
      summary = document.createElement('span');
      summary.className = 'mw-text mw-accumulated-title';
      main.append(summary);
    }

    const helpIcon = heading.querySelector('.mw-tooltip--icon');
    const helpItem = helpIcon?.closest('.mw-stack__item');

    heading.classList.add('joa-option-heading-row');
    main.classList.add('joa-option-heading-main');
    summary.classList.add('joa-option-heading-value');
    main.style.setProperty('display', document.body.classList.contains('joa-product-design') ? 'grid' : 'flex', 'important');
    main.style.setProperty('align-items', 'center', 'important');
    main.style.setProperty('width', '100%', 'important');

    if (helpItem && helpItem !== main) {
      helpItem.classList.add('joa-option-heading-help');
      if (helpItem.parentElement !== main) main.insertBefore(helpItem, summary);
    }

    const selectedValue = selectedOptionValue(option);
    if (summary.textContent.replace(/\s+/g, ' ').trim() !== selectedValue) {
      summary.textContent = selectedValue;
    }

    summary.classList.remove('mw-hidden');
    summary.classList.toggle('joa-option-heading-value--empty', !selectedValue);
    decorateQuickSelector(option, label, summary);
  }

  function decorateOption(option) {
    const name = optionName(option);
    const normalizedName = name.toLowerCase();
    option.dataset.joaOptionName = name;
    option.classList.toggle('joa-option--ring-size', /\bring size\b/i.test(name));

    if (option.matches('.mw-option--type-select') || originalSelect(option)) {
      const select = originalSelect(option);
      select?.style.setProperty('border-radius', 'var(--joa-control-radius)', 'important');
      select?.style.setProperty('font-size', 'var(--joa-pdp-select-font-size, 0.82rem)', 'important');
      option.classList.add('joa-option--select');
      option.classList.remove(
        'joa-option--chips',
        'joa-option--metal',
        'joa-option--image-chips',
        'joa-option--text-chips',
        'joa-option--diamond-size',
        'joa-option--clarity',
        'joa-option--band-width'
      );
      decorateOptionHeading(option);
      return;
    }

    const values = [...option.querySelectorAll('.mw-option__value[data-value-id]')];
    const labels = values.map(optionValueLabel).filter(Boolean);
    const isMaterial = MATERIAL_WORDS.test(name) || labels.some((label) => MATERIAL_VALUES.test(label));
    const hasImageSwatches = values.some((value) => value.querySelector('.mw-option__swatch-image, img'));
    const isDiamondSize = normalizedName.includes('diamond size');

    option.classList.toggle('joa-option--metal', isMaterial);
    option.classList.toggle('joa-option--chips', !isMaterial);
    option.classList.toggle('joa-option--image-chips', !isMaterial && hasImageSwatches);
    option.classList.toggle('joa-option--text-chips', !isMaterial && !hasImageSwatches);
    option.classList.toggle('joa-option--diamond-size', isDiamondSize);
    option.classList.toggle('joa-option--clarity', normalizedName.includes('clarity'));
    option.classList.toggle('joa-option--band-width', normalizedName.includes('band width'));
    option.classList.remove('joa-option--select');

    if (isDiamondSize && values.length) {
      const mobileColumns = Math.min(7, Math.max(1, Math.ceil(values.length / 2)));
      option.style.setProperty('--joa-diamond-mobile-columns', mobileColumns);
    } else {
      option.style.removeProperty('--joa-diamond-mobile-columns');
    }

    values.forEach((value) => {
      const labelText = optionValueLabel(value);
      const label = value.querySelector('label');

      if (labelText) {
        value.dataset.joaLabel = labelText;
        value.dataset.joaDisplayLabel = isMaterial
          ? materialOptionAbbreviation(labelText)
          : labelText;

        if (label) {
          label.dataset.joaLabel = labelText;
          label.dataset.joaDisplayLabel = value.dataset.joaDisplayLabel;
        }
      }
    });

    decorateOptionHeading(option);
  }

  function makeConfiguratorHeading(container) {
    if (container.querySelector(':scope > .joa-configurator-heading')) return;

    const heading = document.createElement('div');
    heading.className = 'joa-configurator-heading';
    heading.textContent = 'Customize your ring';
    container.prepend(heading);
  }

  function makeShippingBenefit(host) {
    if (document.querySelector('.joa-design-details')) return;
    const benefitCopy = 'Free Worldwide Shipping - No extra Duties/Tariffs';
    let benefit = document.querySelector('.joa-shipping-benefit');

    if (!benefit) {
      benefit = document.createElement('div');
      benefit.className = 'joa-shipping-benefit';
      benefit.setAttribute('role', 'note');

      const icon = document.createElement('span');
      icon.className = 'joa-shipping-benefit__icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.innerHTML = '<svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.5"/><path d="M3.5 12h17M12 3c2.3 2.45 3.5 5.45 3.5 9S14.3 18.55 12 21M12 3C9.7 5.45 8.5 8.45 8.5 12s1.2 6.55 3.5 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';

      const text = document.createElement('span');
      text.className = 'joa-shipping-benefit__text';
      benefit.append(icon, text);

      const shareButton = host.querySelector(':scope > .mw-share-config-btn');
      host.insertBefore(benefit, shareButton || null);
    }

    const text = benefit.querySelector('.joa-shipping-benefit__text') || benefit.lastElementChild;
    if (text && text.textContent !== benefitCopy) text.textContent = benefitCopy;
  }

  function arrangeProductSave() {
    const details = document.querySelector('#MainContent .product-details');
    const price = details?.querySelector('product-price');
    const original = details?.querySelector('.joa-design-tools [data-joa-save]');
    const target = details?.querySelector('.joa-configurator-heading') || price;
    if (!target || !original) return;
    let button = details.querySelector('.joa-product-save');
    if (!button) {
      button = document.createElement('button');
      button.type = 'button';
      button.className = 'joa-product-save';
      button.innerHTML = '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8Z"/></svg>';
      button.addEventListener('click', () => {
        details.querySelector('.joa-design-tools [data-joa-save]')?.click();
      });
    }
    if (button.parentElement !== target) target.append(button);
    for (const name of ['aria-label', 'aria-pressed']) {
      const value = original.getAttribute(name);
      if (value && button.getAttribute(name) !== value) button.setAttribute(name, value);
    }
  }

  function arrangeProductHeader() {
    const grid = document.querySelector('#MainContent .product-information__grid');
    const details = grid?.querySelector('.product-details');
    const price = details?.querySelector('product-price');
    const media = grid?.querySelector(':scope > .product-information__media');
    let mobileHeader = grid?.querySelector(':scope > .joa-mobile-product-header');
    const titleBlock = details?.querySelector('.text-block:has(h1)') ||
      mobileHeader?.querySelector('.text-block:has(h1)');
    const reviewBlock = details?.querySelector(
      '.shopify-app-block:has(.reputon-google-reviews-widget)'
    ) || mobileHeader?.querySelector('.shopify-app-block');

    if (!details || !price || !titleBlock) return;

    // Keep the real heading and app widget, including their events, in one place.
    // Mark their original positions so a desktop resize restores the same nodes.
    [titleBlock, reviewBlock].filter(Boolean).forEach((block) => {
      if (!details.contains(block)) return;
      let home = productHeaderHomes.get(block);
      if (!home?.isConnected) {
        home = document.createComment('joa-product-header-home');
        block.before(home);
        productHeaderHomes.set(block, home);
      }
    });
    const titleHome = productHeaderHomes.get(titleBlock);
    const headerGroup = titleHome?.parentElement || details;
    const descriptionBlock = [
      ...(headerGroup?.querySelectorAll('rte-formatter.text-block, .text-block.rte') || [])
    ].find((element) => element !== titleBlock && !element.querySelector('h1'));
    const descriptionToggle =
      descriptionBlock?.nextElementSibling?.matches('.joa-product-description-toggle')
        ? descriptionBlock.nextElementSibling
        : headerGroup?.querySelector('.joa-product-description-toggle');

    if (mobileProductLayout.matches && media) {
      if (!mobileHeader) {
        mobileHeader = document.createElement('div');
        mobileHeader.className = 'joa-mobile-product-header';
        grid.prepend(mobileHeader);
      }

      // A section refresh may supply new blocks; never retain an older heading.
      [...mobileHeader.children].forEach((block) => {
        if (block !== titleBlock && block !== reviewBlock) block.remove();
      });
      [titleBlock, reviewBlock].filter(Boolean).forEach((block) => {
        if (block.parentElement !== mobileHeader) mobileHeader.append(block);
      });

      const priceAnchor = descriptionToggle || descriptionBlock;
      if (priceAnchor && priceAnchor.nextElementSibling !== price) {
        priceAnchor.insertAdjacentElement('afterend', price);
      }
      return;
    }

    [titleBlock, reviewBlock].filter(Boolean).forEach((block) => {
      const home = productHeaderHomes.get(block);
      if (home?.isConnected && block.parentElement === mobileHeader) home.after(block);
    });
    mobileHeader?.remove();

    if (reviewBlock) {
      if (titleBlock.nextElementSibling !== reviewBlock) {
        titleBlock.insertAdjacentElement('afterend', reviewBlock);
      }
    }

    const priceAnchor = descriptionToggle || descriptionBlock || reviewBlock || titleBlock;
    if (priceAnchor.nextElementSibling !== price) {
      priceAnchor.insertAdjacentElement('afterend', price);
    }
  }

  function updateOptionsLoadingState() {
    if (document.querySelector('.joa-earrings-product, .joa-bracelet-product, .joa-necklace-product')) {
      document.querySelector('.joa-options-loading')?.remove();
      document.body.classList.remove('joa-options-are-loading');
      return;
    }
    const apoContainer = primaryOptionContainer();
    const optionsReady = Boolean(
      apoContainer?.querySelector(':scope > .mw-option .mw-option__control')
    );
    const html = document.documentElement;
    const decisionPending =
      html.classList.contains('joa-express-booting') ||
      (html.classList.contains('joa-express-active') && !optionsReady);
    const form =
      apoContainer?.closest('form') ||
      document.querySelector('.shopify-product-form') ||
      document.querySelector('form[action*="/cart/add"]');
    let loadingState = document.querySelector('.joa-options-loading');

    if (optionsReady || !decisionPending) {
      loadingState?.remove();
      document.body.classList.remove('joa-options-are-loading');
      return;
    }

    if (!form) return;

    if (!loadingState) {
      loadingState = document.createElement('div');
      loadingState.className = 'joa-options-loading';
      loadingState.setAttribute('role', 'status');
      loadingState.setAttribute('aria-live', 'polite');
      loadingState.innerHTML = [
        '<span class="joa-options-loading__spinner" aria-hidden="true"></span>',
        '<span>Preparing your ring customizations…</span>'
      ].join('');

      const purchaseButtons = form.querySelector(':scope > .product-form-buttons');
      form.insertBefore(loadingState, purchaseButtons || null);
    }

    document.body.classList.add('joa-options-are-loading');
  }

  function decorateServiceLinks() {
    const productForm =
      primaryOptionContainer()?.closest('form') ||
      document.querySelector('.shopify-product-form') ||
      document.querySelector('form[action*="/cart/add"]');

    if (productForm) productForm.classList.add('joa-product-form');

    document.querySelectorAll('.product-service-links').forEach((links) => {
      const phoneLink = links.querySelector('a[href^="tel:"]');
      const chatLink = links.querySelector('a[href^="sms:"]');
      const bookingLink = links.querySelector('a[href*="setmore.com"]');

      const updateLink = (link, iconName, iconMarkup, labelText = '', detailText = '') => {
        if (!link) return;

        const icon = link.querySelector('.product-service-icon');
        const label = link.querySelector(':scope > span:not(.product-service-icon)');

        if (icon && icon.dataset.joaIcon !== iconName) {
          icon.innerHTML = iconMarkup;
          icon.dataset.joaIcon = iconName;
        }

        if (label) {
          label.classList.add('product-service-copy');

          let title = label.querySelector(':scope > .product-service-copy__title');
          if (!title) {
            label.textContent = '';
            title = document.createElement('span');
            title.className = 'product-service-copy__title';
            label.append(title);
          }

          if (title.textContent !== labelText) title.textContent = labelText;

          let detail = label.querySelector(':scope > .product-service-copy__detail');
          if (detailText && !detail) {
            detail = document.createElement('span');
            detail.className = 'product-service-copy__detail';
            label.append(detail);
          }

          if (detail && detail.textContent !== detailText) detail.textContent = detailText;
          if (!detailText) detail?.remove();
        }
      };

      updateLink(
        phoneLink,
        'phone',
        '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M7.15 3.5 10 6.35 8.35 8.8c1.45 2.95 3.9 5.4 6.85 6.85L17.65 14 20.5 16.85l-.9 2.45c-.32.88-1.2 1.42-2.13 1.3C9.77 19.64 4.36 14.23 3.4 6.53c-.12-.93.42-1.81 1.3-2.13l2.45-.9Z" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>',
        'ORDER BY PHONE',
        '647-691-9838'
      );

      updateLink(
        chatLink,
        'chat',
        '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 5.25h16v11.5H9.25L4 20v-3.25H4V5.25Z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><circle cx="8.25" cy="11" r=".8" fill="currentColor"/><circle cx="12" cy="11" r=".8" fill="currentColor"/><circle cx="15.75" cy="11" r=".8" fill="currentColor"/></svg>',
        'CHAT WITH A JEWELER'
      );

      updateLink(
        bookingLink,
        'appointment',
        '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="3.5" y="5.5" width="17" height="15" rx="2" stroke="currentColor" stroke-width="1.6"/><path d="M8 3.5v4M16 3.5v4M3.5 10h17M8 15.25l2.1 2.1 5-5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>',
        'BOOK AN APPOINTMENT'
      );

      links.classList.add('joa-product-service-links');

      if (productForm && links.parentElement !== productForm) {
        const sourceSection = links.closest('.shopify-section');
        if (sourceSection && !productForm.contains(sourceSection)) {
          sourceSection.classList.add('joa-service-links-source');
        }
        productForm.append(links);
      }
    });
  }

  function decorateShareLink() {
    const shareButton = document.querySelector('.mw-share-config-btn');
    if (!shareButton) return;

    shareButton.classList.add('joa-share-ring-link');
    shareButton.setAttribute('aria-label', 'Share your ring configuration');

    if (!shareButton.querySelector('.mw-share-config-btn__icon')) {
      const icon = document.createElement('span');
      icon.className = 'mw-share-config-btn__icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.innerHTML = '<svg viewBox="0 0 24 24" fill="none"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      shareButton.prepend(icon);
    }
  }

  function decorateReviewsSection() {
    const widget = document.querySelector(
      '.reputon-google-reviews-widget[data-type="carousel"]'
    );
    const block = widget?.closest('.shopify-app-block');
    if (!widget || !block) return;

    block.classList.add('joa-reviews-section');

    let intro = block.querySelector(':scope > .joa-reviews-intro');
    if (!intro) {
      intro = document.createElement('div');
      intro.className = 'joa-reviews-intro';
      intro.innerHTML = [
        '<div class="joa-reviews-intro__heading">',
        '<h2>Customer Reviews</h2>',
        '</div>',
        '<div class="joa-reviews-summary">',
        '<span class="joa-reviews-summary__score"></span>',
        '<span class="joa-reviews-summary__details">',
        '<span class="joa-reviews-summary__stars" aria-hidden="true">★★★★★</span>',
        '<span class="joa-reviews-summary__count"></span>',
        '</span>',
        '</div>',
        '<a class="joa-reviews-all" href="/pages/jewels-of-toronto-reviews" target="_blank" rel="noopener noreferrer">',
        '<span>Read all reviews</span>',
        '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 15 15 9M10 9h5v5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><path d="M19 13v5a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>',
        '</a>'
      ].join('');
      block.insertBefore(intro, widget);
    }

    const score = widget.querySelector('.reputon-count-number')?.textContent?.trim() || '';
    const rawCount = widget.querySelector('.reputon-reviews-count')?.textContent || '';
    const reviewCount = rawCount.match(/[\d,]+/)?.[0] || '';
    const reviewsLink = widget.querySelector('.reputon-reviews-head a[href]')?.href || '';
    const scoreNode = intro.querySelector('.joa-reviews-summary__score');
    const countNode = intro.querySelector('.joa-reviews-summary__count');
    const allReviewsLink = intro.querySelector('.joa-reviews-all');
    const countCopy = reviewCount
      ? `Based on ${reviewCount} Google reviews`
      : 'Verified Google reviews';

    if (scoreNode && scoreNode.textContent !== score) scoreNode.textContent = score;
    if (countNode && countNode.textContent !== countCopy) countNode.textContent = countCopy;
    if (allReviewsLink && reviewsLink && allReviewsLink.href !== reviewsLink) {
      allReviewsLink.href = reviewsLink;
    }
  }

  function decorateProductAccordions() {
    document.querySelectorAll('#MainContent .accordion').forEach((accordion) => {
      if (!accordion.querySelector('accordion-custom details')) return;

      accordion
        .closest('.shopify-section')
        ?.classList.add('joa-product-accordions-section');
    });
  }

  function decorateDownpay() {
    const fullLabel = document.querySelector("#downpay-block label[for='purchase-option-full']");
    const depositLabel = document.querySelector("#downpay-block label[for='purchase-option-deposit-input']");

    if (depositLabel && !depositLabel.dataset.joaDecorated) {
      const titleText = depositLabel.textContent.replace(/\s+/g, ' ').trim() || '10% Deposit';
      depositLabel.textContent = '';

      const title = document.createElement('span');
      title.className = 'joa-downpay-title';
      title.textContent = titleText;

      const subtitle = document.createElement('span');
      subtitle.className = 'joa-downpay-subtitle';
      subtitle.dataset.joaDepositPrice = '';

      depositLabel.append(title, subtitle);
      depositLabel.dataset.joaDecorated = 'true';
    }

    if (fullLabel && !fullLabel.dataset.joaDecorated) {
      const titleText = fullLabel.textContent.replace(/\s+/g, ' ').trim() || 'Pay in full';
      fullLabel.textContent = '';

      const title = document.createElement('span');
      title.className = 'joa-downpay-title';
      title.textContent = titleText;

      const subtitle = document.createElement('span');
      subtitle.className = 'joa-downpay-subtitle';
      subtitle.dataset.joaFullPrice = '';

      fullLabel.append(title, subtitle);
      fullLabel.dataset.joaDecorated = 'true';
    }

    const fullPrice = document.querySelector('[data-joa-full-price]');
    const depositAmount = document.querySelector('[data-joa-deposit-price]');
    const priceText = selectedProductPrice();
    if (fullPrice && priceText && fullPrice.textContent !== priceText) {
      fullPrice.textContent = priceText;
    }

    const breakdown = purchaseBreakdown(priceText);
    const design = document.body.classList.contains('joa-product-design');
    const depositPriceText = breakdown ? `${breakdown.now} now, ${breakdown.later} later` : '';
    if (depositAmount && depositPriceText && depositAmount.textContent !== depositPriceText) {
      if (design) {
        const now = document.createElement('span');
        now.textContent = `${breakdown.now} now, `;
        const later = document.createElement('span');
        later.textContent = `${breakdown.later} later`;
        depositAmount.replaceChildren(now, later);
      } else {
        depositAmount.textContent = depositPriceText;
      }
    }
  }

  function enhance() {
    if (pendingAdd && feedbackState === 'loading' &&
      [...document.querySelectorAll('.product-details .mw-option__helptext.mw-text--style-negative')]
        .some((error) => error.getClientRects().length && getComputedStyle(error).visibility !== 'hidden')) {
      handleCartError();
    }
    scheduled = false;

    document.body.classList.add('joa-product-page');
    syncCartFeedback();
    arrangeProductHeader();
    syncPrimaryPrice();
    arrangeProductSave();
    decorateDownpay();
    syncStickyPrice();
    decorateServiceLinks();
    decorateShareLink();
    decorateProductAccordions();
    decorateReviewsSection();
    updateOptionsLoadingState();

    const containers = [...document.querySelectorAll(OPTION_SELECTOR)].filter(
      (container) =>
        !container.closest('.joa-express-form') &&
        container.querySelector('[name^="mw_opts["]')
    );
    const fallbackForm = document.querySelector('.shopify-product-form');

    if (!containers.length) {
      if (fallbackForm) {
        fallbackForm.classList.add('joa-product-form');
        makeShippingBenefit(fallbackForm);
        document.body.classList.add('joa-product-page-ready');
      }
      document.dispatchEvent(new CustomEvent('joa:product-enhanced'));
      return;
    }

    containers.forEach((container) => {
      const form = container.closest('form');
      if (form) form.classList.add('joa-product-form');

      const options = [...container.querySelectorAll(':scope > .mw-option')];
      options.forEach((option) => option.classList.remove('joa-option--last'));
      options.forEach(decorateOption);

      if (options.length) {
        options[options.length - 1].classList.add('joa-option--last');
      }

      makeConfiguratorHeading(container);
      makeShippingBenefit(container);
    });

    updateOptionsLoadingState();
    document.body.classList.add('joa-product-page-ready');
    document.dispatchEvent(new CustomEvent('joa:product-enhanced'));
  }

  function scheduleEnhance() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(enhance);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleEnhance, { once: true });
  } else {
    scheduleEnhance();
  }

  document.addEventListener('change', scheduleEnhance);
  document.addEventListener('joa:options-designed', arrangeProductSave);
  mobileProductLayout.addEventListener('change', scheduleEnhance);
  document.addEventListener('joa:apo-statechange', scheduleEnhance);
  document.addEventListener('joa:price-verified', decorateDownpay);
  document.addEventListener('joa:price-verified', scheduleEnhance);
  /* Window capture runs before the theme's document-capture sticky-cart
   * forwarder, so non-pointer activation cannot add a second item while the
   * confirmed state is visible. */
  window.addEventListener('click', blockConfirmedButtonActivation, true);
  window.addEventListener('keydown', blockConfirmedButtonActivation, true);
  window.addEventListener('click', markAddToCartIntent, true);
  document.addEventListener('submit', markAddToCartIntent, true);

  function handleCartError() {
    if (pendingAdd) window.clearTimeout(pendingAdd.expiryTimer);
    pendingAdd = null;
    setCartFeedback('idle');
    primeCartSnapshot();
  }

  async function handleStandardCartLinesUpdate(event) {
    if (event.action !== 'add' || !event.promise) return;

    try {
      const result = await event.promise;
      const data = result?.detail || {};
      const resource = {
        item_count: Number(result?.cart?.totalQuantity),
        items: Array.isArray(data.items) ? data.items : []
      };

      await showCartToast({
        detail: {
          resource,
          sourceId: data.sourceId,
          data
        }
      });
    } catch (_error) {
      handleCartError();
    }
  }

  document.addEventListener('joa:cart-add-error', handleCartError);
  document.addEventListener('cart:error', handleCartError);
  document.addEventListener('shopify:cart:error', handleCartError);
  document.addEventListener('cart:update', showCartToast);
  document.addEventListener('shopify:cart:lines-update', handleStandardCartLinesUpdate);

  primeCartSnapshot();
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) handleCartError();
    if (pendingAdd) return;
    cartSnapshot = null;
    primeCartSnapshot();
  });

  const observer = new MutationObserver(scheduleEnhance);
  observer.observe(document.querySelector('#MainContent') || document.body, {
    childList: true,
    subtree: true,
    characterData: true
  });

  window.setTimeout(scheduleEnhance, 300);
  window.setTimeout(scheduleEnhance, 1000);
  window.setTimeout(scheduleEnhance, 3000);
})();
